/**
 * Single sign-on provider (OIDC, authorization code + PKCE S256).
 *
 * Boots with a silent session check: if the browser already holds a session
 * for the realm the app authenticates without a redirect; otherwise the app
 * stays anonymous until `signIn()` is invoked, which sends the user to the
 * hosted sign-in page.
 *
 * Naming is generic on purpose; the underlying library is an implementation
 * detail and never surfaces in UI, URLs or storage keys.
 */

import Keycloak from 'keycloak-js';
import { AUTH_AUTHORITY, AUTH_REALM, AUTH_CLIENT_ID } from './authConfig';

let provider: Keycloak | null = null;

/** Path this app is served from (e.g. `/admin/` in production). */
function basePath(): string {
  return import.meta.env.BASE_URL || '/';
}

function appOrigin(): string {
  return window.location.origin + basePath();
}

/**
 * Initialize the provider and perform a silent session check.
 * @returns whether an active session was found.
 */
export async function initSso(): Promise<boolean> {
  provider = new Keycloak({ url: AUTH_AUTHORITY, realm: AUTH_REALM, clientId: AUTH_CLIENT_ID });

  const authenticated = await provider.init({
    onLoad: 'check-sso',
    pkceMethod: 'S256',
    silentCheckSsoRedirectUri: `${window.location.origin}${basePath()}silent-check-sso.html`,
    checkLoginIframe: false,
  });

  if (authenticated) {
    // Renew reactively when the token expires...
    provider.onTokenExpired = () => {
      void ensureValidToken();
    };
    // ...and, more importantly, proactively. This app is meant to stay open all
    // day waiting for calls, and two things break a purely reactive renewal:
    // browsers throttle timers in background tabs, so the expiry callback can
    // fire late; and the session at the identity service expires on its own
    // after a period with no activity, which a silent tab would otherwise reach.
    // A periodic renewal keeps the token fresh AND resets that inactivity
    // window, so an open tab never loses its session.
    startProactiveRenewal();
  }

  return Boolean(authenticated);
}

// How often to consider renewing, and how much validity to guarantee. The
// interval must stay well below both the token lifetime and the inactivity
// window at the identity service.
const RENEWAL_INTERVAL_MS = 60 * 1000;
const MIN_VALIDITY_SECONDS = 120;

let renewalTimer: ReturnType<typeof setInterval> | null = null;
let renewalInFlight: Promise<boolean> | null = null;

function startProactiveRenewal(): void {
  if (renewalTimer) clearInterval(renewalTimer);
  renewalTimer = setInterval(() => {
    void ensureValidToken();
  }, RENEWAL_INTERVAL_MS);

  // A tab coming back to the foreground may have missed several intervals.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void ensureValidToken();
  });
}

/**
 * Guarantee a token that is valid for at least the next couple of minutes,
 * renewing it when it is not. Concurrent callers share a single renewal so a
 * burst of requests cannot start a stampede.
 *
 * @returns whether a usable token is available afterwards.
 */
export async function ensureValidToken(): Promise<boolean> {
  if (!provider?.authenticated) return false;
  if (renewalInFlight) return renewalInFlight;

  renewalInFlight = provider
    .updateToken(MIN_VALIDITY_SECONDS)
    .then((refreshed) => {
      if (refreshed && provider?.token) onTokenRefreshed(provider.token);
      return true;
    })
    .catch(() => {
      // The session is genuinely gone (revoked, or inactive for too long).
      // Sending the user to sign in again is the only honest outcome.
      signIn();
      return false;
    })
    .finally(() => {
      renewalInFlight = null;
    });

  return renewalInFlight;
}

/** Callback invoked whenever a fresh access token is obtained. */
let tokenListener: ((token: string) => void) | null = null;
export function onToken(listener: (token: string) => void): void {
  tokenListener = listener;
}
function onTokenRefreshed(token: string): void {
  if (tokenListener) tokenListener(token);
}

export function getAccessToken(): string | undefined {
  return provider?.token;
}

export function getTokenParsed(): Record<string, unknown> | undefined {
  return provider?.tokenParsed as Record<string, unknown> | undefined;
}

/** Send an unauthenticated user to the hosted sign-in page. */
export function signIn(): void {
  provider?.login({ redirectUri: appOrigin() });
}

/** End the session at the identity provider and return to the app root. */
export async function signOut(): Promise<void> {
  if (!provider) return;
  // The post-logout return URI must match a registered value exactly: the
  // identity provider does not treat a trailing slash as equivalent, so a
  // `/admin/` value is rejected and the session is never ended. Strip the
  // trailing slash so the returned URI matches the registered base.
  const returnUri = appOrigin().replace(/\/$/, '');
  await provider.logout({ redirectUri: returnUri });
}
